import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { apiClient } from "~/lib/orpc-client";
import { orpc } from "~/lib/orpc";
import { Country, State } from "country-state-city";
import { useRef } from "react";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { Loader2 } from "lucide-react";

const COUNTRIES = Country.getAllCountries();

const baseFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  country: z.string().min(2, "Country is required"),
  state: z.string().optional().or(z.literal("")),
});

const universityFormSchema = baseFormSchema.extend({
  country: z.string().optional().or(z.literal("")),
});

type PersonalisationFormData = z.infer<typeof universityFormSchema>;

export default function PersonalisationModal() {
  const queryClient = useQueryClient();
  const hasInitialized = useRef(false);
  const { user: meData, isUniversityAccount, organizationName } = useAuthenticatedUser();

  const formSchema = isUniversityAccount ? universityFormSchema : baseFormSchema;

  const form = useForm<PersonalisationFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      country: "",
      state: "",
    },
  });

  // Only initialize form once when data is loaded
  if (meData && !hasInitialized.current) {
    hasInitialized.current = true;
    form.reset({
      firstName: meData.firstName ?? "",
      lastName: meData.lastName ?? "",
      country: meData.country ?? "",
      state: meData.state ?? "",
    });
  }

  const { mutate: updateProfile, isPending: isUpdating } = useMutation({
    mutationFn: (input: Parameters<typeof apiClient.users.update>[0]) =>
      apiClient.users.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries(orpc.users.getMe.queryOptions());
    },
  });

  const onSubmit = (data: PersonalisationFormData) => {
    const toNullable = (v: string | undefined) =>
      v && v.length > 0 ? v : null;
    updateProfile({
      firstName: toNullable(data.firstName),
      lastName: toNullable(data.lastName),
      country: toNullable(data.country),
      state: toNullable(data.state),
    });
  };

  // Check if required fields are filled - if so, don't show the modal
  const hasRequiredFields = isUniversityAccount
    ? meData?.firstName && meData?.lastName
    : meData?.firstName && meData?.lastName && meData?.country;

  if (hasRequiredFields) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-lg text-center w-full max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Welcome! Let&apos;s personalize your account</CardTitle>
          <CardDescription>
            Please complete your profile to get started. This helps us provide
            you with a better experience.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First name*</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last name*</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {!isUniversityAccount && (
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country*</FormLabel>
                      <FormControl>
                        <select
                          className="w-full border rounded-lg h-10 px-3 bg-white"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <option value="">Select a country</option>
                          {COUNTRIES.map((c) => (
                            <option key={c.isoCode} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {!isUniversityAccount && (
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State or Province</FormLabel>
                      <FormControl>
                        {(() => {
                          const selected = COUNTRIES.find(
                            (c) => c.name === (form.watch("country") ?? "")
                          );
                          const states = selected
                            ? State.getStatesOfCountry(selected.isoCode)
                            : [];
                          return (
                            <select
                              className="w-full border rounded-lg h-10 px-3 bg-white"
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              disabled={!selected}
                            >
                              <option value="">
                                {selected
                                  ? "Select a state"
                                  : "Select a country first"}
                              </option>
                              {states.map((s) => (
                                <option key={s.isoCode} value={s.name}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          );
                        })()}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isUniversityAccount && (
                <div role="note" className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-left space-y-3">
                  <p className="text-sm text-black leading-relaxed">
                    Your Proudly Human results will not be shared with{" "}
                    {organizationName ?? "your university"} or anyone else.
                  </p>
                </div>
              )}

              <div className="pt-2">
                <Button
                  className="w-full"
                  type="submit"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    "Continue"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
